// Беседы (README §2): CLI-окно одной сессии на всю рабочую область
// (panes/cli/CliSession на общем каркасе CliWindow/CliLog/CliInput) и дерево
// сессий в меню: «фоновые» (беседы сторожей «Фон · …», их ведёт tg-router —
// Степан отвечает им из бота), «в работе» (черновики разделов и «вопрос по
// заметке» — первыми), «+ новая сессия», «история» — 5 последних с датой, «показать все».

import { useState } from 'react'
import CliSession from '../../panes/cli/CliSession'
import ErrorBoundary from '../../shell/ErrorBoundary'
import { useAppStore } from '../../store/appStore'
import { NOTE_HINT, useCtxNote } from './ctxNote'
import { EDIT_HINT, NEW_HINT } from '../custom/hints'
import type { SessionInfo, SessionState } from '../../protocol'
import { SideMenu } from '../../ui/SideMenu'
import type { SideItem, SideTone } from '../../ui/SideMenu'
import type { SectionModule } from '../types'

const STATE_LABEL: Record<SessionState, string> = {
  idle: 'ожидает',
  running: 'работает',
  waiting_permission: 'ждёт ответа',
  exited: 'завершена',
}
const STATE_TONE: Record<SessionState, SideTone> = {
  idle: 'dim',
  running: 'dim',
  waiting_permission: 'warn',
  exited: 'dim',
}

/** Дата сессии в истории: «вчера», «19 сен», сегодня — время. */
export function sessionDate(at: number, now = new Date()): string {
  const d = new Date(at < 1e12 ? at * 1000 : at)
  if (Number.isNaN(d.getTime())) return ''
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  }
  const y = new Date(now)
  y.setDate(now.getDate() - 1)
  if (d.toDateString() === y.toDateString()) return 'вчера'
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }).replace('.', '')
}

/** Черновики разделов и «вопрос по заметке» стоят в «в работе» первыми (§2). */
function draftFirst(a: SessionInfo, b: SessionInfo): number {
  const w = (s: SessionInfo) => (/^(правка:|новый раздел|вопрос по заметке)/.test(s.title) ? 0 : 1)
  return w(a) - w(b) || b.lastActivity - a.lastActivity
}

function Sidebar() {
  const sessions = useAppStore((s) => s.sessions)
  const activeId = useAppStore((s) => s.activeId)
  const section = useAppStore((s) => s.section)
  const focusSession = useAppStore((s) => s.focusSession)
  const newSession = useAppStore((s) => s.newSession)
  const [all, setAll] = useState(false)

  const isBg = (s: SessionInfo) => /^Фон ·/.test(s.title)
  const background = sessions
    .filter((s) => s.role !== 'telegram' && isBg(s))
    .sort((a, b) => b.lastActivity - a.lastActivity)
  const mine = sessions.filter((s) => s.role !== 'telegram' && !isBg(s))
  const live = mine.filter((s) => s.state !== 'exited').sort(draftFirst)
  const history = mine
    .filter((s) => s.state === 'exited')
    .sort((a, b) => b.lastActivity - a.lastActivity)
  const shown = all ? history : history.slice(0, 5)
  const onChats = section === 'chats'

  const row = (s: SessionInfo, withState: boolean): SideItem => ({
    id: s.id,
    label: s.title || 'без названия',
    title: s.title,
    live: s.state !== 'exited',
    active: onChats && s.id === activeId,
    meta: withState ? STATE_LABEL[s.state] : sessionDate(s.lastActivity),
    metaTone: withState ? STATE_TONE[s.state] : 'dim',
    onSelect: () => focusSession(s.id),
    actions: withState
      ? [{
          // живая беседа: остановить процесс — уезжает в «историю», лог остаётся
          id: 'close',
          label: '×',
          title: 'закрыть беседу — уйдёт в историю',
          confirm: s.state === 'running' || s.state === 'waiting_permission' ? 'остановить?' : undefined,
          run: () => void useAppStore.getState().killSession(s.id).then(() => useAppStore.getState().refreshSessions()),
        }]
      : [{
          // история: удалить насовсем вместе с логом
          id: 'purge',
          label: '×',
          title: 'удалить насовсем',
          danger: true,
          confirm: 'удалить?',
          run: () => void useAppStore.getState().purgeSession(s.id),
        }],
  })

  const items: SideItem[] = [
    ...(background.length
      ? [{
          id: 'background',
          label: 'фоновые',
          meta: String(background.length),
          // выселенная беседа сторожа тоже здесь: tg-router поднимет её на ответ из бота
          children: background.map((s) => ({ ...row(s, s.state !== 'exited'), label: s.title.replace(/^Фон ·\s*/, '') })),
        }]
      : []),
    {
      id: 'live',
      label: 'в работе',
      meta: String(live.length),
      children: [
        ...live.map((s) => row(s, true)),
        { id: 'new', label: '+ новая сессия', action: true, onSelect: () => void newSession() },
      ],
    },
    {
      id: 'history',
      label: 'история',
      meta: String(history.length),
      children: [
        ...shown.map((s) => row(s, false)),
        ...(history.length > 5
          ? [{
              id: 'all',
              label: all ? 'свернуть' : `показать все ${history.length}`,
              action: true,
              onSelect: () => setAll((v) => !v),
            }]
          : []),
      ],
    },
  ]

  return (
    <SideMenu
      screen="chats"
      sections={[{ id: 'sessions', title: 'Сессии', onAdd: () => void newSession(), addTitle: 'новая сессия', items }]}
    />
  )
}

/** Подсказка в поле ввода: `appStore.sessionHints` (её кладут «+»/«✎» разделов
 *  и «спросить в беседе» у заметки); после перезагрузки подсказок в сторе нет —
 *  тогда по названию беседы. */
/** Пустая беседа-сборщик (§10.1 п.2, §10.2 п.2): вопрос агента рисует панель —
 *  первая реплика за оператором, тратить на вопрос ход модели незачем. */
function builderAsk(title: string | undefined): string | null {
  if (!title) return null
  if (title.startsWith('новый раздел')) return 'Опишите раздел: что он показывает, откуда берёт данные и какие действия в нём нужны.'
  if (title.startsWith('правка:')) return `Что поменять в разделе «${title.slice('правка:'.length).trim()}»? Правки пойдут в черновик.`
  return null
}

function placeholderFor(hint: string | undefined, title: string | undefined): string | undefined {
  if (hint) return hint
  if (!title) return undefined
  if (title.startsWith('новый раздел')) return NEW_HINT
  if (title.startsWith('правка:')) return EDIT_HINT
  if (title.startsWith('вопрос по заметке')) return NOTE_HINT
  return undefined
}

/** Какую беседу показывать: выбранную, иначе последнюю открытую, иначе свежую живую. */
function useCurrentSession(): string | null {
  const activeId = useAppStore((s) => s.activeId)
  const paneIds = useAppStore((s) => s.paneIds)
  const sessions = useAppStore((s) => s.sessions)
  const known = (id: string | null | undefined) => !!id && sessions.some((x) => x.id === id && x.role !== 'telegram')
  if (known(activeId)) return activeId
  const open = [...paneIds].reverse().find((id) => known(id))
  if (open) return open
  const live = sessions
    .filter((x) => x.role !== 'telegram' && x.state !== 'exited')
    .sort((a, b) => b.lastActivity - a.lastActivity)[0]
  return live?.id ?? null
}

function View() {
  const current = useCurrentSession()
  const session = useAppStore((s) => s.sessions.find((x) => x.id === current) ?? null)
  const hint = useAppStore((s) => (current ? s.sessionHints[current] : undefined))
  const newSession = useAppStore((s) => s.newSession)
  const creating = useAppStore((s) => s.creating)
  const note = useCtxNote((s) => (current ? s.notes[current] ?? null : null))
  const setNote = useCtxNote((s) => s.setNote)
  const markSent = useCtxNote((s) => s.markSent)

  if (!current) {
    return (
      <div className="cli-empty">
        <span>⏺ Нет открытой сессии.</span>
        <button type="button" disabled={creating} onClick={() => void newSession()}>
          {creating ? 'создаю…' : '+ новая сессия'}
        </button>
      </div>
    )
  }
  return (
    <ErrorBoundary key={current} scope={`беседа ${current.slice(0, 6)}`} compact>
      <CliSession
        sessionId={current}
        placeholder={placeholderFor(hint, session?.title)}
        ctxNote={note}
        onDropNote={note ? () => setNote(current, null) : undefined}
        onNoteSent={() => markSent(current)}
        empty={note ? (
          <div className="t-row t-row--agent t-row--head">
            <span className="t-gut">⏺</span>
            <span className="t-txt">Заметка «{note.title}» в контексте. Спросите что-нибудь о ней.</span>
          </div>
        ) : builderAsk(session?.title) ? (
          <div className="t-row t-row--agent t-row--head">
            <span className="t-gut">⏺</span>
            <span className="t-txt">{builderAsk(session?.title)}</span>
          </div>
        ) : null}
      />
    </ErrorBoundary>
  )
}

function useRail() {
  const sessions = useAppStore((s) => s.sessions)
  const mine = sessions.filter((s) => s.role !== 'telegram')
  const bg = mine.filter((s) => /^Фон ·/.test(s.title)).length
  const live = mine.filter((s) => s.state !== 'exited' && !/^Фон ·/.test(s.title)).length
  return {
    count: String(mine.length),
    tip: `сессии: ${bg} фоновых, ${live} в работе, ${mine.length - bg - live} в истории`,
  }
}

const chats: SectionModule = { Sidebar, View, keepAlive: true, useRail }
export default chats
